import { ConfigValue, FormConfig } from '@components/common/formSchema/types';
import { IDestDefinition } from '@stores/destinationDefsList';

import { FormElement, Group, SdkTemplateSubGroup, SdkTemplateGroup, isMultiKeyField } from './types';

/**
 * Applies the same source-scoping logic as the inner loop of
 * `getConfigTemplateFields` but operating on a single `SdkTemplateSubGroup.fields`
 * array. Returns the flat `FormElement[]` for the given sourceType.
 */
const getConfigTemplateFieldsForSubGroup = (
  subGroup: SdkTemplateSubGroup,
  destDef: IDestDefinition,
  config: FormConfig,
  sourceType: string,
): FormElement[] => {
  const defaultFields: FormElement[] = [];
  const sourceSpecificFields: FormElement[] = [];

  subGroup.fields.forEach((field) => {
    if (isMultiKeyField(field)) {
      defaultFields.push(field);
      return;
    }
    const isFormField = field.type !== 'redirect';
    const isSourceSpecific =
      isFormField &&
      destDef.config.destConfig[sourceType] &&
      destDef.config.destConfig[sourceType].includes(field.configKey);
    const isDefault =
      isFormField &&
      destDef.config.destConfig.defaultConfig &&
      destDef.config.destConfig.defaultConfig.includes(field.configKey);

    if (isSourceSpecific) {
      const modifiedField = { ...field };
      modifiedField.configKey = `${sourceType}-${field.configKey}`;
      const value = config[field.configKey] as Record<string, ConfigValue>;
      if (value !== undefined && value[sourceType] !== undefined) {
        modifiedField.value = value[sourceType] as typeof modifiedField.value;
      } else if ('default' in field && field.default !== undefined) {
        // No saved per-source value yet → seed the field's declared default so
        // the control reflects it (e.g. a checkbox whose default is `true`).
        modifiedField.value = field.default as typeof modifiedField.value;
      }
      sourceSpecificFields.push(modifiedField);
    }

    if (isDefault) {
      const modifiedField = { ...field };
      const value = config[field.configKey] as ConfigValue;
      if (value !== undefined) {
        modifiedField.value = value as typeof modifiedField.value;
      } else if ('default' in field && field.default !== undefined) {
        modifiedField.value = field.default as typeof modifiedField.value;
      }
      defaultFields.push(modifiedField);
    }

    if (field.type === 'redirect') {
      const modifiedField = { ...field };
      modifiedField.sourceType = sourceType;
      sourceSpecificFields.push(modifiedField);
    }
  });

  return defaultFields.concat(sourceSpecificFields);
};

/**
 * Builds the named sub-group `Group`s for ONE source type from an
 * `SdkTemplateGroup.groups` array. Each non-empty `SdkTemplateSubGroup` becomes a
 * `Group` carrying its own title/note/icon/callout/conditions, with
 * source-scoped fields and the standard device/hybrid `preRequisites` gate.
 * Empty sub-groups (no fields scoped to this source type) are omitted.
 *
 * These Groups are appended to `sdkGroups` alongside the flat-fields Group, so
 * `sdkTemplate.groups` simply adds more Groups into the "Destination settings"
 * section — exactly like `sdkTemplate.fields` does — rather than a new Section.
 */
export const getFormGroupsFromSdkTemplateSubGroups = (
  sdkTemplate: SdkTemplateGroup,
  destDef: IDestDefinition,
  config: FormConfig,
  sourceType: string,
): Group[] => {
  const groups: Group[] = [];

  (sdkTemplate.groups ?? []).forEach((subGroup) => {
    const scopedFields = getConfigTemplateFieldsForSubGroup(subGroup, destDef, config, sourceType);
    if (scopedFields.length === 0) {
      return; // omit empty sub-groups for this source type
    }

    groups.push({
      title: subGroup.title,
      note: subGroup.note,
      isSubGroup: true,
      ...(subGroup.icon !== undefined ? { icon: subGroup.icon } : {}),
      ...(subGroup.callout !== undefined ? { callout: subGroup.callout } : {}),
      ...(subGroup.conditions !== undefined ? { conditions: subGroup.conditions } : {}),
      fields: scopedFields,
      preRequisites: {
        fields: [
          { configKey: `${sourceType}-connectionMode`, value: 'device' },
          { configKey: `${sourceType}-connectionMode`, value: 'hybrid' },
        ],
        condition: 'or',
      },
    });
  });

  return groups;
};
